/*
 * Drives background videos inside an image banner.
 *
 * Playback is JS-driven rather than using the `autoplay` attribute so that we can:
 *  - honour `prefers-reduced-motion: reduce` (video stays on its poster frame),
 *  - only download and play the video for the viewport that is actually showing,
 *  - stop playback while the banner is scrolled out of view,
 *  - expose a real play/pause control (WCAG 2.2.2 Pause, Stop, Hide).
 *
 * With JS unavailable the videos never load and their poster images act as the banner.
 */
class BannerVideo extends HTMLElement {
  connectedCallback() {
    this.banner = this.closest('.banner');
    this.button = this.querySelector('.banner__video-toggle');
    this.videos = this.banner ? Array.from(this.banner.querySelectorAll('video[data-banner-video]')) : [];

    if (!this.button || this.videos.length === 0) return;

    this.desktopQuery = window.matchMedia('(min-width: 750px)');
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.autoplayEnabled = this.dataset.autoplay !== 'false';
    this.paused = !this.autoplayEnabled || this.motionQuery.matches;
    this.inViewport = false;

    this.onToggle = this.onToggle.bind(this);
    this.onBreakpointChange = this.onBreakpointChange.bind(this);
    this.onMotionPreferenceChange = this.onMotionPreferenceChange.bind(this);
    this.syncButton = this.syncButton.bind(this);

    this.setBannerTop = this.setBannerTop.bind(this);
    this.setBannerTop();
    window.addEventListener('resize', this.setBannerTop);

    this.button.addEventListener('click', this.onToggle);
    this.desktopQuery.addEventListener('change', this.onBreakpointChange);
    this.motionQuery.addEventListener('change', this.onMotionPreferenceChange);
    this.videos.forEach((video) => {
      video.addEventListener('play', this.syncButton);
      video.addEventListener('pause', this.syncButton);
    });

    // The control is hidden until now: without JS it could not do anything.
    this.classList.add('banner__video-controls--ready');

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          this.inViewport = entries.some((entry) => entry.isIntersecting);
          this.updatePlayback();
        },
        { rootMargin: '200px 0px', threshold: 0 }
      );
      this.observer.observe(this.banner);
    } else {
      this.inViewport = true;
    }

    this.updatePlayback();
    this.syncButton();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    window.removeEventListener('resize', this.setBannerTop);
    if (this.button) this.button.removeEventListener('click', this.onToggle);
    if (this.desktopQuery) this.desktopQuery.removeEventListener('change', this.onBreakpointChange);
    if (this.motionQuery) this.motionQuery.removeEventListener('change', this.onMotionPreferenceChange);
    if (this.videos) {
      this.videos.forEach((video) => {
        video.removeEventListener('play', this.syncButton);
        video.removeEventListener('pause', this.syncButton);
      });
    }
  }

  /*
   * The video matching the current viewport. Mirrors the CSS: a video marked `all`
   * is shown at every width because no narrower alternative was set.
   */
  /*
   * Feeds the CSS height cap. A banner that starts within the first screen is capped
   * to the space left below it; one further down the page gets the full window.
   */
  setBannerTop() {
    if (!this.banner) return;
    const top = this.banner.getBoundingClientRect().top + window.scrollY;
    this.banner.style.setProperty('--banner-top', `${top < window.innerHeight ? top : 0}px`);
  }

  get activeVideo() {
    const wanted = this.desktopQuery.matches ? 'desktop' : 'mobile';
    return (
      this.videos.find((video) => video.dataset.breakpoint === wanted) ||
      this.videos.find((video) => video.dataset.breakpoint === 'all') ||
      null
    );
  }

  updatePlayback() {
    const active = this.activeVideo;

    this.videos.forEach((video) => {
      if (video !== active) this.pauseVideo(video);
    });

    if (!active) return;

    if (this.inViewport && !this.paused) {
      this.playVideo(active);
    } else {
      this.pauseVideo(active);
    }
  }

  playVideo(video) {
    const played = video.play();

    // Browsers can still refuse (data saver, low power mode). Reflect that in the control.
    if (played && typeof played.catch === 'function') {
      played.catch(() => {
        this.paused = true;
        this.syncButton();
      });
    }
  }

  pauseVideo(video) {
    if (!video.paused) video.pause();
  }

  onToggle() {
    const active = this.activeVideo;
    if (!active) return;

    this.paused = !active.paused;

    if (this.paused) {
      this.pauseVideo(active);
    } else {
      this.inViewport = true;
      this.playVideo(active);
    }

    this.syncButton();
  }

  onBreakpointChange() {
    this.updatePlayback();
  }

  onMotionPreferenceChange(event) {
    if (event.matches) this.paused = true;
    this.updatePlayback();
    this.syncButton();
  }

  syncButton() {
    const active = this.activeVideo;
    const playing = active ? !active.paused : false;

    this.button.setAttribute('aria-label', playing ? this.dataset.pauseLabel : this.dataset.playLabel);
    this.classList.toggle('banner__video-controls--paused', !playing);
  }
}

customElements.define('banner-video', BannerVideo);
